import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, BookOpen, CheckCircle2 } from "lucide-react";
import { ClientPageLayout } from "@/components/client/ClientPageLayout";
import ProfileIconLink from "@/components/ProfileIconLink";
import { completeLesson, getLessonById, type LessonRow } from "@/integrations/backend/lessons";
import { useToast } from "@/hooks/use-toast";
import { stripInlineFontSize } from "@/lib/html";
import { queryKeys } from "@/lib/query-keys";

const LessonDetailPage = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [lesson, setLesson] = useState<LessonRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingComplete, setMarkingComplete] = useState(false);

  useEffect(() => {
    const fetchLesson = async () => {
      if (!lessonId) {
        setError("Lesson ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const fetchedLesson = await getLessonById(lessonId);
        setLesson(fetchedLesson);
      } catch (err) {
        console.error("Failed to fetch lesson:", err);
        setError("Failed to load lesson. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchLesson();
  }, [lessonId]);

  function getVideoEmbed(url: string): { type: "iframe"; src: string } | null {
    const yt = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/i.exec(url);
    if (yt?.[1]) {
      return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
    }
    const vimeo = /vimeo\.com\/(\d+)/i.exec(url);
    if (vimeo?.[1]) {
      return { type: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}` };
    }
    return null;
  }

  const handleBack = () => {
    navigate("/explore");
  };

  const handleMarkCompleted = () => {
    if (!lesson || lesson.is_completed || markingComplete) return;
    setMarkingComplete(true);
    completeLesson(lesson.id)
      .then((result) => {
        setLesson((prev) =>
          prev
            ? {
                ...prev,
                is_completed: result.is_completed,
                completed_at: result.completed_at,
              }
            : prev
        );
        const lessonsListKey = queryKeys.lessons.list(false);
        queryClient.setQueryData<LessonRow[]>(lessonsListKey, (prev) =>
          prev?.map((l) =>
            l.id === lesson.id
              ? { ...l, is_completed: result.is_completed, completed_at: result.completed_at }
              : l
          )
        );
        void queryClient.invalidateQueries({ queryKey: lessonsListKey });
        toast({
          title: "Lesson marked as completed",
          description: "Your progress has been saved.",
        });
      })
      .catch((err) => {
        console.error("Failed to mark lesson as completed:", err);
        toast({
          title: "Unable to mark lesson complete",
          description: "Please try again.",
        });
      })
      .finally(() => {
        setMarkingComplete(false);
      });
  };

  if (loading) {
    return (
      <ClientPageLayout activeTab="explore" contentClassName="flex items-center justify-center min-h-[50vh]">
        <div className="text-white text-center">
          <p>Loading lesson...</p>
        </div>
      </ClientPageLayout>
    );
  }

  if (error || !lesson) {
    return (
      <ClientPageLayout activeTab="explore" contentClassName="flex items-center justify-center min-h-[50vh]">
        <Card className="border border-primary/20 bg-dark-card p-6 max-w-md">
          <CardContent className="text-white text-center">
            <p className="mb-4">{error || "Lesson not found"}</p>
            <Button onClick={handleBack} variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-black">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Explore
            </Button>
          </CardContent>
        </Card>
      </ClientPageLayout>
    );
  }

  const videoEmbed = lesson.video_url ? getVideoEmbed(lesson.video_url) : null;
  const isCompleted = lesson.is_completed;

  return (
    <ClientPageLayout
      activeTab="explore"
      header={
        <>
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              size="sm"
              className="bg-white/10 rounded-full h-9 px-3 flex items-center justify-center hover:bg-white/20"
              onClick={handleBack}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="text-sm">Back to Learning Hub</span>
            </Button>
            <ProfileIconLink />
          </div>
          <div className="flex items-center gap-2 mb-6">
            <BookOpen className="w-6 h-6 text-primary shrink-0" />
            <h1 className="text-2xl font-bold">{lesson.title}</h1>
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleMarkCompleted}
              disabled={isCompleted || markingComplete}
              className="gradient-primary text-black"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {isCompleted ? "Completed" : markingComplete ? "Saving..." : "Mark as completed"}
            </Button>
          </div>
        </>
      }
    >
      {videoEmbed ? (
        <Card className="border border-primary/20 bg-dark-card overflow-hidden">
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <iframe
              src={videoEmbed.src}
              title={lesson.title}
              className="absolute top-0 left-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
        </Card>
      ) : lesson.thumbnail_file ? (
        <Card className="border border-primary/20 bg-dark-card overflow-hidden">
          <img
            src={lesson.thumbnail_file}
            alt={lesson.title}
            className="w-full h-auto object-cover"
          />
        </Card>
      ) : null}

      {lesson.infographic_file && (
        <Card className="border border-primary/20 bg-dark-card overflow-hidden">
          <CardContent className="p-0">
            <img
              src={lesson.infographic_file}
              alt={`${lesson.title} infographic`}
              className="w-full h-auto object-contain"
            />
          </CardContent>
        </Card>
      )}

      <Card className="border border-primary/20 bg-dark-card">
        <CardContent className="p-4 sm:p-6 text-white">
          <div
            className="lesson-article"
            dangerouslySetInnerHTML={{ __html: stripInlineFontSize(lesson.article) }}
          />
        </CardContent>
      </Card>

      <Card className="border border-primary/20 bg-dark-card">
        <CardContent className="flex items-center justify-between gap-3 p-4 text-white">
          <div>
            <p className="text-sm font-medium">Lesson status</p>
            <p className="text-xs text-gray-light">
              {isCompleted ? "This lesson is complete." : "This lesson is incomplete."}
            </p>
          </div>
          <div
            className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
              isCompleted
                ? "border-transparent bg-green-500 text-white"
                : "border-primary/20 bg-white/5 text-white"
            }`}
          >
            {isCompleted ? "Complete" : "Incomplete"}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center">
        <Button
          onClick={handleBack}
          variant="outline"
          className="border-primary/30 text-primary hover:bg-primary hover:text-black"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Learning Hub
        </Button>
      </div>
    </ClientPageLayout>
  );
};

export default LessonDetailPage;
